import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { useBranding } from '../contexts/BrandingContext';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { 
  Zap, 
  LayoutDashboard, 
  FileText, 
  Settings, 
  CreditCard, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Globe,
  Shield,
  ShieldCheck,
  ChevronDown,
  RadarIcon,
  Users,
  Brain,
  Layers,
  TrendingUp,
  MapPin,
  ShieldAlert,
  Search,
  Database,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { apiPost } from '../lib/apiClient';

const Sidebar = ({ user, setUser, activePage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('decoded_sidebar_collapsed') === 'true';
  });
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('decoded_sidebar_expanded');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const { t } = useLanguage();
  const { flags } = useFeatureFlags();
  const { branding } = useBranding();

  const isAdmin = ['admin', 'owner'].includes(user?.role);

  // Derive active item from URL for precise highlighting
  const getActiveItemId = () => {
    const pathname = location.pathname;
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const mode = params.get('mode');

    if (pathname === '/dashboard') return 'dashboard';
    if (pathname === '/analyzer' || pathname.startsWith('/analyzer')) {
      if (tab === 'sourcing') return 'sourcing';
      if (tab === 'origin') return 'origin';
      if (tab === 'financial') return 'financial';
      if (tab === 'erp') return 'erp-analyzer';
      if (mode === 'bulk') return 'bulk-analysis';
      return 'analyzer';
    }
    if (pathname === '/refund-radar') return 'refund-radar';
    if (pathname === '/ieepa-recovery') return 'ieepa';
    if (pathname === '/governance') return 'governance';
    if (pathname === '/duty-risk-monitoring') return 'duty-risk';
    if (pathname === '/history') return 'history';
    if (pathname === '/organization') return 'organization';
    if (pathname === '/admin') return 'admin';
    return activePage;
  };

  const activeItemId = getActiveItemId();

  // Navigation structure (with feature flag gating)
  const allNavGroups = [
    {
      id: 'analyze-products',
      label: 'Analyze Products',
      icon: Search,
      children: [
        { id: 'analyzer', label: 'Product Analyzer', icon: Sparkles, href: '/analyzer', flag: 'single_analysis' },
        { id: 'bulk-analysis', label: 'Bulk Upload', icon: Layers, href: '/analyzer?mode=bulk', flag: 'bulk_analysis' },
        { id: 'erp-analyzer', label: 'ERP Import', icon: Database, href: '/analyzer?tab=erp', premium: true, flag: 'erp_import' },
      ]
    },
    {
      id: 'supply-chain',
      label: 'Supply Chain',
      icon: Globe,
      children: [
        { id: 'sourcing', label: 'Smart Sourcing', icon: MapPin, href: '/analyzer?tab=sourcing', flag: 'sourcing_optimizer' },
        { id: 'origin', label: 'Origin & Preference', icon: Zap, href: '/analyzer?tab=origin', flag: 'origin_engine' },
        { id: 'supply-chain-intel', label: 'Intelligence', icon: BarChart3, href: '/supply-chain-intelligence' },
      ]
    },
    {
      id: 'recover-duties',
      label: 'Recover Duties',
      icon: RadarIcon,
      children: [
        { id: 'refund-radar', label: 'Refund Radar', icon: RadarIcon, href: '/refund-radar', flag: 'refund_radar' },
        { id: 'ieepa', label: 'IEEPA Recovery', icon: ShieldAlert, href: '/ieepa-recovery' },
      ]
    },
    {
      id: 'governance-risk',
      label: 'Governance & Risk',
      icon: Shield,
      children: [
        { id: 'governance', label: 'Governance', icon: Shield, href: '/governance', flag: 'governance' },
        { id: 'duty-risk', label: 'Duty Risk Monitoring', icon: ShieldAlert, href: '/duty-risk-monitoring', flag: 'duty_monitoring' },
      ]
    },
  ];

  // Standalone items (promoted from single-child groups)
  const standaloneTools = [
    { id: 'financial', label: 'Financial Impact', icon: TrendingUp, href: '/analyzer?tab=financial', flag: 'financial_analyzer' },
  ];

  const standaloneBottom = [
    { id: 'history', label: 'Analysis History', icon: FileText, href: '/history' },
  ];

  // Filter out disabled features
  const navGroups = useMemo(() => {
    return allNavGroups.map(group => ({
      ...group,
      children: group.children.filter(item => !item.flag || flags[item.flag] !== false),
    })).filter(group => group.children.length > 0);
  }, [flags]);

  // Filter standalone tools by feature flags
  const filteredStandaloneTools = useMemo(() => {
    return standaloneTools.filter(item => !item.flag || flags[item.flag] !== false);
  }, [flags]);

  // Check if a group contains the active item
  const isGroupActive = (group) => group.children.some(c => c.id === activeItemId);

  // Auto-expand groups that contain the active item
  useEffect(() => {
    const activeGroup = navGroups.find(g => isGroupActive(g));
    if (activeGroup && !expandedGroups[activeGroup.id]) {
      setExpandedGroups(prev => ({ ...prev, [activeGroup.id]: true }));
    }
  }, [activeItemId]);

  // Persist expanded state
  useEffect(() => {
    localStorage.setItem('decoded_sidebar_expanded', JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Save collapsed state
  useEffect(() => {
    localStorage.setItem('decoded_sidebar_collapsed', collapsed.toString());
    window.dispatchEvent(new CustomEvent('sidebarToggle', { detail: { collapsed } }));
  }, [collapsed]);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleLogout = async () => {
    try {
      await apiPost('/api/auth/logout', undefined, { auth: false });
    } catch {}
    localStorage.removeItem('decoded_token');
    localStorage.removeItem('decoded_user');
    if (setUser) setUser(null);
    toast.success('Logged out successfully');
    navigate('/');
  };

  const toggleCollapse = () => setCollapsed(!collapsed);

  // Render a single nav item (child item inside a group)
  const NavItem = ({ item, isCollapsed, depth = 0 }) => {
    const isActive = activeItemId === item.id;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={item.href}
            className={`flex items-center gap-3 rounded-lg transition-all duration-200 group relative
              ${depth > 0 && !isCollapsed ? 'pl-10 pr-4 py-2' : 'px-4 py-2.5'}
              ${isActive
                ? item.premium
                  ? 'bg-gradient-to-r from-[#8B5CF6]/10 to-[#EC4899]/10 text-[#8B5CF6] font-medium'
                  : 'bg-gradient-to-r from-[#2563EB]/10 to-[#06B6D4]/10 text-[#2563EB] font-medium'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }
              ${isCollapsed ? 'justify-center px-3' : ''}
            `}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.id}`}
          >
            <item.icon className={`w-[18px] h-[18px] flex-shrink-0 transition-colors ${
              isActive
                ? item.premium ? 'text-[#8B5CF6]' : 'text-[#2563EB]'
                : item.premium ? 'text-[#8B5CF6]/60 group-hover:text-[#8B5CF6]' : 'text-slate-400 group-hover:text-slate-600'
            }`} />
            {!isCollapsed && (
              <>
                <span className={`truncate text-sm ${item.premium && !isActive ? 'text-slate-700' : ''}`}>{item.label}</span>
                {item.premium && (
                  <span className="ml-auto mr-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white flex-shrink-0">Pro</span>
                )}
                {isActive && <ChevronRight className={`w-3.5 h-3.5 ml-auto flex-shrink-0 ${item.premium ? 'text-[#8B5CF6]' : 'text-[#2563EB]'}`} />}
              </>
            )}
          </Link>
        </TooltipTrigger>
        {isCollapsed && (
          <TooltipContent side="right" className="bg-slate-900 text-white border-0">
            <p className="font-medium text-xs">{item.label}</p>
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  // Render a collapsible group
  const NavGroup = ({ group, isCollapsed }) => {
    const groupActive = isGroupActive(group);
    const isExpanded = expandedGroups[group.id];
    const GroupIcon = group.icon;

    if (isCollapsed) {
      // In collapsed mode, show group icon; clicking navigates to first child
      const firstChild = group.children[0];
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={firstChild.href}
              className={`flex items-center justify-center px-3 py-2.5 rounded-lg transition-all duration-200 group
                ${groupActive
                  ? 'bg-gradient-to-r from-[#2563EB]/10 to-[#06B6D4]/10 text-[#2563EB]'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                }
              `}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-group-${group.id}`}
            >
              <GroupIcon className={`w-5 h-5 flex-shrink-0 ${groupActive ? 'text-[#2563EB]' : 'text-slate-400 group-hover:text-slate-600'}`} />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-slate-900 text-white border-0 p-0 overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-700">
              <p className="font-semibold text-xs">{group.label}</p>
            </div>
            <div className="py-1">
              {group.children.map(child => (
                <Link
                  key={child.id}
                  to={child.href}
                  className={`flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-800 transition-colors
                    ${activeItemId === child.id ? 'text-cyan-400' : 'text-slate-300'}
                  `}
                  onClick={() => setMobileOpen(false)}
                >
                  <child.icon className="w-3.5 h-3.5" />
                  {child.label}
                </Link>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      );
    }

    // Expanded sidebar: show collapsible group
    return (
      <div data-testid={`nav-group-${group.id}`}>
        <button
          onClick={() => toggleGroup(group.id)}
          className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 group
            ${groupActive
              ? 'text-[#2563EB]'
              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
            }
          `}
          data-testid={`nav-group-toggle-${group.id}`}
        >
          <GroupIcon className={`w-[18px] h-[18px] flex-shrink-0 ${groupActive ? 'text-[#2563EB]' : 'text-slate-400 group-hover:text-slate-600'}`} />
          <span className={`text-sm truncate flex-1 text-left ${groupActive ? 'font-semibold' : 'font-medium'}`}>{group.label}</span>
          <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''} ${groupActive ? 'text-[#2563EB]' : 'text-slate-400'}`} />
        </button>
        <div className={`overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="ml-4 border-l border-slate-100 mt-0.5 mb-1 space-y-0.5">
            {group.children.map(child => (
              <NavItem key={child.id} item={child} isCollapsed={false} depth={1} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Standalone nav item (Dashboard, Team, Admin)
  const StandaloneItem = ({ item, isCollapsed }) => {
    const isActive = activeItemId === item.id;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={item.href}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 group relative
              ${isActive
                ? 'bg-gradient-to-r from-[#2563EB]/10 to-[#06B6D4]/10 text-[#2563EB] font-medium'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }
              ${isCollapsed ? 'justify-center px-3' : ''}
            `}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.id}`}
          >
            <item.icon className={`w-5 h-5 flex-shrink-0 transition-colors ${isActive ? 'text-[#2563EB]' : 'text-slate-400 group-hover:text-slate-600'}`} />
            {!isCollapsed && (
              <>
                <span className="truncate text-sm">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto text-[#2563EB] flex-shrink-0" />}
              </>
            )}
          </Link>
        </TooltipTrigger>
        {isCollapsed && (
          <TooltipContent side="right" className="bg-slate-900 text-white border-0">
            <p className="font-medium text-xs">{item.label}</p>
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  const NavContent = ({ isCollapsed = false }) => (
    <TooltipProvider delayDuration={300}>
      <nav className="flex-1 space-y-1 overflow-y-auto">
        {/* Dashboard - standalone */}
        <StandaloneItem
          item={{ id: 'dashboard', label: t('nav.dashboard'), icon: LayoutDashboard, href: '/dashboard' }}
          isCollapsed={isCollapsed}
        />

        {!isCollapsed && <div className="pt-3 pb-1 px-4"><span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Tools</span></div>}
        {isCollapsed && <div className="border-t border-slate-100 my-2 mx-3" />}

        {/* Collapsible groups + standalone tools interleaved */}
        {navGroups.length > 0 && <NavGroup group={navGroups[0]} isCollapsed={isCollapsed} />}

        {/* Financial Impact - standalone (promoted from single-child group) */}
        {filteredStandaloneTools.map(item => (
          <StandaloneItem key={item.id} item={item} isCollapsed={isCollapsed} />
        ))}

        {/* Remaining groups */}
        {navGroups.slice(1).map(group => (
          <NavGroup key={group.id} group={group} isCollapsed={isCollapsed} />
        ))}

        {/* Analysis History - standalone (promoted from single-child group) */}
        {standaloneBottom.map(item => (
          <StandaloneItem key={item.id} item={item} isCollapsed={isCollapsed} />
        ))}

        {!isCollapsed && <div className="pt-3 pb-1 px-4"><span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Workspace</span></div>}
        {isCollapsed && <div className="border-t border-slate-100 my-2 mx-3" />}

        {/* Organization - standalone */}
        <StandaloneItem
          item={{ id: 'organization', label: 'Organization', icon: Users, href: '/organization' }}
          isCollapsed={isCollapsed}
        />

        {/* Admin Portal - standalone, admin only */}
        {isAdmin && (
          <StandaloneItem
            item={{ id: 'admin', label: t('nav.adminPortal'), icon: ShieldCheck, href: '/admin' }}
            isCollapsed={isCollapsed}
          />
        )}
      </nav>

      {/* Plan Badge */}
      <div className="border-t border-slate-100 pt-4 mt-4">
        {!isCollapsed ? (
          <div className="px-4">
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl px-4 py-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#2563EB]" />
                  <span className="text-xs text-slate-500 font-jakarta">Plan</span>
                </div>
                <span className="text-xs font-semibold text-[#2563EB] capitalize font-jakarta">
                  {user?.subscription_tier || 'Starter'}
                </span>
              </div>
              {user?.org && (
                <p className="text-[10px] text-slate-400 truncate mt-0.5" data-testid="sidebar-org-name" title={user.org.org_name}>
                  {user.org.org_name}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="w-10 h-10 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl flex items-center justify-center" title={`Plan: ${user?.subscription_tier || 'Starter'}`}>
              <Sparkles className="w-4 h-4 text-[#2563EB]" />
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );

  return (
    <>
      {/* Full-Width Top Header */}
      <div className="hidden lg:flex fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-100 z-50 items-center justify-between px-6">
        <Link to="/dashboard" className="flex items-center gap-3">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt={branding.display_name || 'Logo'} className="h-10 w-10 rounded-xl object-contain" />
          ) : (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: `linear-gradient(135deg, var(--brand-accent, #2563EB), var(--brand-accent-light, #06B6D4))` }}>
              <Zap className="w-5 h-5 text-white" />
            </div>
          )}
          <span className="font-outfit font-bold text-xl text-slate-900">{branding.display_name || 'Decoded'}</span>
        </Link>

        <div className="relative" ref={userDropdownRef}>
          <button
            onClick={() => setUserDropdownOpen(!userDropdownOpen)}
            className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors"
            data-testid="user-menu-btn"
          >
            <div className="w-9 h-9 bg-gradient-to-br from-[#2563EB]/10 to-[#06B6D4]/10 rounded-xl flex items-center justify-center flex-shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-xl object-cover" />
              ) : (
                <span className="font-outfit font-semibold text-[#2563EB] text-sm">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              )}
            </div>
            <div className="text-left hidden sm:block">
              <p className="font-medium text-slate-900 text-sm font-jakarta">{user?.name}</p>
              <p className="text-xs text-slate-500 font-jakarta">{user?.email}</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {userDropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-100 py-2 z-50">
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="font-medium text-slate-900 font-jakarta">{user?.name}</p>
                <p className="text-sm text-slate-500 font-jakarta">{user?.email}</p>
                <div className="mt-2 inline-flex items-center gap-1.5 bg-gradient-to-r from-[#2563EB]/10 to-[#06B6D4]/10 text-[#2563EB] px-2 py-1 rounded-lg text-xs font-semibold capitalize">
                  <Sparkles className="w-3 h-3" />
                  {user?.subscription_tier || 'Starter'} Plan
                </div>
                {user?.org && (
                  <p className="text-xs text-slate-500 mt-1 truncate" data-testid="dropdown-org-name">
                    {user.org.org_name} &middot; <span className="capitalize">{user.org.user_role?.replace('_', ' ')}</span>
                  </p>
                )}
              </div>
              <div className="py-1">
                <button onClick={() => { navigate('/preferences'); setUserDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors text-left" data-testid="dropdown-settings">
                  <Settings className="w-4 h-4 text-slate-400" />
                  <span className="font-jakarta text-sm">Preferences</span>
                </button>
                <button onClick={() => { navigate('/corrections'); setUserDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors text-left" data-testid="dropdown-corrections">
                  <Brain className="w-4 h-4 text-slate-400" />
                  <span className="font-jakarta text-sm">My Corrections</span>
                </button>
                <button onClick={() => { navigate('/billing'); setUserDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors text-left" data-testid="dropdown-billing">
                  <CreditCard className="w-4 h-4 text-slate-400" />
                  <span className="font-jakarta text-sm">{t('nav.billingPlans')}</span>
                </button>
                {isAdmin && (
                  <button onClick={() => { navigate('/admin'); setUserDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 transition-colors text-left" data-testid="dropdown-admin">
                    <ShieldCheck className="w-4 h-4 text-slate-400" />
                    <span className="font-jakarta text-sm">Admin Portal</span>
                  </button>
                )}
              </div>
              <div className="border-t border-slate-100 pt-1">
                <button onClick={() => { handleLogout(); setUserDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors text-left" data-testid="dropdown-logout">
                  <LogOut className="w-4 h-4" />
                  <span className="font-jakarta text-sm">{t('nav.signOut')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#06B6D4] flex items-center justify-center shadow-md">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-lg text-slate-900">Decoded</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              className="p-2 text-slate-600 rounded-xl hover:bg-slate-100 transition-colors"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-[#2563EB]/10 to-[#06B6D4]/10 rounded-lg flex items-center justify-center">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-lg object-cover" />
                ) : (
                  <span className="font-outfit font-semibold text-[#2563EB] text-xs">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 text-slate-600 rounded-xl hover:bg-slate-100 transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile User Dropdown */}
        {userDropdownOpen && (
          <div className="absolute right-4 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-100 py-2 z-50">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="font-medium text-slate-900 font-jakarta">{user?.name}</p>
              <p className="text-sm text-slate-500 font-jakarta">{user?.email}</p>
              <div className="mt-2 inline-flex items-center gap-1.5 bg-gradient-to-r from-[#2563EB]/10 to-[#06B6D4]/10 text-[#2563EB] px-2 py-1 rounded-lg text-xs font-semibold capitalize">
                <Sparkles className="w-3 h-3" />
                {user?.subscription_tier || 'Starter'} Plan
              </div>
            </div>
            <div className="py-1">
              <button onClick={() => { navigate('/preferences'); setUserDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 text-left">
                <Settings className="w-4 h-4 text-slate-400" />
                <span className="text-sm">Preferences</span>
              </button>
              <button onClick={() => { navigate('/corrections'); setUserDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 text-left">
                <Brain className="w-4 h-4 text-slate-400" />
                <span className="text-sm">My Corrections</span>
              </button>
              <button onClick={() => { navigate('/billing'); setUserDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-slate-50 text-left">
                <CreditCard className="w-4 h-4 text-slate-400" />
                <span className="text-sm">{t('nav.billingPlans')}</span>
              </button>
            </div>
            <div className="border-t border-slate-100 pt-1">
              <button onClick={() => { handleLogout(); setUserDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 text-left">
                <LogOut className="w-4 h-4" />
                <span className="text-sm">{t('nav.signOut')}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-slate-900/20 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`lg:hidden fixed top-14 left-0 bottom-0 z-50 w-72 bg-white border-r border-slate-100 transform transition-transform duration-300 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full p-5">
          <NavContent isCollapsed={false} />
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex fixed top-16 left-0 bottom-0 bg-white border-r border-slate-100 flex-col p-5 transition-all duration-300 ${
          collapsed ? 'w-20' : 'w-72'
        }`}
        data-testid="desktop-sidebar"
      >
        <NavContent isCollapsed={collapsed} />

        <button
          onClick={toggleCollapse}
          className="absolute -right-3 top-8 w-6 h-6 bg-white border border-slate-200 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors shadow-sm"
          data-testid="sidebar-collapse-toggle"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>

      {/* Spacers */}
      <div className="flex">
        <div className="lg:hidden h-14 w-full" />
      </div>
      <div className="hidden lg:flex">
        <div className={`flex-shrink-0 transition-all duration-300 ${collapsed ? 'w-20' : 'w-72'}`} />
      </div>
    </>
  );
};

export default Sidebar;
